import { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Send, Upload, Loader2, User, Users, FileText, RefreshCw, MessageSquare, Eye } from "lucide-react";
import api from "@/dashboard/api";

const fieldCls =
  "w-full border border-brand-border rounded-lg px-4 py-3 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15";
const labelCls = "block text-xs font-display font-semibold uppercase tracking-wider text-brand-slate mb-2";

// Replace {{1}}, {{2}}, ... placeholders with provided params. Missing params
// stay as {{n}} so recruiters immediately see what's not yet filled in.
const renderTemplate = (body, params) => {
  if (!body) return "";
  return body.replace(/\{\{(\d+)\}\}/g, (m, n) => {
    const idx = parseInt(n, 10) - 1;
    const val = params[idx];
    return val && val.trim() !== "" ? val : m;
  });
};

const buildFullMessage = (t, params) => {
  if (!t) return "";
  const parts = [];
  if (t.header_text) parts.push(renderTemplate(t.header_text, params));
  if (t.body_text) parts.push(renderTemplate(t.body_text, params));
  if (t.footer_text) parts.push(t.footer_text);
  return parts.join("\n\n");
};

const PreviewBubble = ({ text, empty }) => (
  <div data-testid="preview-bubble" className="bg-[#e7ffdb] border border-[#c9e7b8] rounded-2xl rounded-tl-md px-4 py-3 shadow-sm whitespace-pre-wrap text-sm text-brand-ink leading-relaxed max-w-md">
    {text || <span className="italic text-brand-muted">{empty || "Select a template to preview the message"}</span>}
  </div>
);

const SendMessages = () => {
  const [tab, setTab] = useState("single");

  // templates from Meta
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState("");

  // single
  const [to, setTo] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [lang, setLang] = useState("en_US");
  const [params, setParams] = useState("");
  const [preview, setPreview] = useState("");
  const [sending, setSending] = useState(false);

  // bulk
  const [bulkTemplate, setBulkTemplate] = useState("");
  const [bulkLang, setBulkLang] = useState("en_US");
  const [file, setFile] = useState(null);
  const [csvRows, setCsvRows] = useState([]); // parsed rows for preview
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileRef = useRef(null);

  const errMsg = (e) => e.response?.data?.detail || "Something went wrong.";

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    setTemplatesError("");
    try {
      const { data } = await api.get("/whatsapp/templates");
      setTemplates(data.templates || []);
      if (!data.templates?.length) setTemplatesError("No templates found in your Meta account.");
    } catch (e) {
      setTemplatesError(errMsg(e));
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => { loadTemplates(); }, []);

  // Templates grouped by name+language
  const templateOptions = templates.map((t) => ({
    value: `${t.name}::${t.language}`,
    label: `${t.name} · ${t.language}${t.status && t.status !== "APPROVED" ? ` (${t.status})` : ""}`,
    template: t,
  }));

  const findTemplate = (name, language) => templates.find((t) => t.name === name && t.language === language);

  const selectedSingleTemplate = useMemo(() => findTemplate(templateName, lang), [templates, templateName, lang]);
  const selectedBulkTemplate = useMemo(() => findTemplate(bulkTemplate, bulkLang), [templates, bulkTemplate, bulkLang]);

  const singleParamsArr = useMemo(
    () => params.split(",").map((p) => p.trim()),
    [params]
  );

  const singlePreviewText = useMemo(
    () => buildFullMessage(selectedSingleTemplate, singleParamsArr),
    [selectedSingleTemplate, singleParamsArr]
  );

  const sendSingle = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const body_params = params.split(",").map((p) => p.trim()).filter(Boolean);
      await api.post("/whatsapp/send-template", {
        to, template_name: templateName, language_code: lang, body_params, preview_text: preview || null,
      });
      toast.success(`Template sent to ${to}`);
      setTo(""); setParams(""); setPreview("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  // ---- Bulk CSV parsing for preview ----
  const parseCsvForPreview = (text) => {
    const lines = text.replace(/^\ufeff/, "").split(/\r?\n/).filter((l) => l.trim() !== "");
    if (lines.length === 0) return [];
    const parseRow = (line) => {
      // simple CSV split (no embedded commas in this recruiter use-case)
      return line.split(",").map((c) => c.trim());
    };
    const headers = parseRow(lines[0]).map((h) => h.toLowerCase());
    const rows = lines.slice(1).map((l) => {
      const cells = parseRow(l);
      const row = {};
      headers.forEach((h, i) => { row[h] = cells[i] || ""; });
      return row;
    });
    return rows.map((row) => {
      const phone = row.phone || row.phone_number || row.number || row.mobile || "";
      const paramKeys = Object.keys(row)
        .filter((k) => /^param\d+$/.test(k))
        .sort((a, b) => parseInt(a.slice(5), 10) - parseInt(b.slice(5), 10));
      let params = paramKeys.map((k) => row[k]).filter((v) => v !== "");
      if (params.length === 0 && row.name) params = [row.name];
      return { phone, params };
    }).filter((r) => r.phone);
  };

  const handleFileChange = async (f) => {
    setFile(f);
    setCsvRows([]);
    if (!f) return;
    try {
      const text = await f.text();
      const rows = parseCsvForPreview(text);
      setCsvRows(rows);
    } catch {
      /* ignore preview failure - backend will still validate */
    }
  };

  const sendBulk = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please select a CSV file");
    setBulkSending(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("template_name", bulkTemplate);
      fd.append("language_code", bulkLang);
      fd.append("file", file);
      const { data } = await api.post("/whatsapp/send-template-bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      toast.success(`${data.sent} sent, ${data.failed} failed`);
      setFile(null);
      setCsvRows([]);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBulkSending(false);
    }
  };

  const downloadSampleCsv = () => {
    const csv = "phone,param1,param2,param3\n919876543210,John,Monday 10am,Bengaluru\n919812345678,Priya,Tuesday 2pm,Remote\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "whatsapp-bulk-sample.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const bulkPreviewRows = csvRows.slice(0, 3);
  const bulkPreviewMore = Math.max(0, csvRows.length - 3);

  return (
    <div data-testid="send-messages-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl text-brand-ink mb-1">Send Messages</h1>
          <p className="text-brand-muted">Send approved WhatsApp template messages with a live preview.</p>
        </div>
        <button
          type="button"
          onClick={loadTemplates}
          data-testid="reload-templates"
          className="inline-flex items-center gap-2 border border-brand-border bg-white rounded-lg px-4 py-2 text-sm font-display font-semibold text-brand-slate hover:border-brand-blue transition-colors"
        >
          {loadingTemplates ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Reload templates
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab("single")}
          data-testid="tab-single"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-display font-medium text-sm transition-colors ${tab === "single" ? "bg-brand-blue text-white" : "bg-white border border-brand-border text-brand-slate"}`}
        >
          <User className="w-4 h-4" /> Single
        </button>
        <button
          onClick={() => setTab("bulk")}
          data-testid="tab-bulk"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-display font-medium text-sm transition-colors ${tab === "bulk" ? "bg-brand-blue text-white" : "bg-white border border-brand-border text-brand-slate"}`}
        >
          <Users className="w-4 h-4" /> Bulk (CSV)
        </button>
      </div>

      {templatesError && (
        <div data-testid="templates-error" className="mb-6 border border-amber-200 bg-amber-50 text-amber-900 rounded-lg px-4 py-3 text-sm">
          {templatesError} <span className="text-brand-muted">(You can still type a template name manually below — but the live preview requires templates loaded from Meta.)</span>
        </div>
      )}

      {tab === "single" ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <form onSubmit={sendSingle} data-testid="single-send-form" className="lg:col-span-3 bg-white rounded-2xl border border-brand-border p-6 md:p-8 card-soft">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Candidate Phone (intl format)</label>
                <input required value={to} onChange={(e) => setTo(e.target.value)} placeholder="919876543210" className={fieldCls} data-testid="single-to" />
              </div>
              <div>
                <label className={labelCls}>Template</label>
                <select
                  required
                  value={templateName ? `${templateName}::${lang}` : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) { setTemplateName(""); return; }
                    const [n, l] = v.split("::");
                    setTemplateName(n);
                    setLang(l);
                  }}
                  data-testid="single-template"
                  className={fieldCls}
                >
                  <option value="">— select an approved template —</option>
                  {templateOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>
                  Body Params (comma-separated)
                  {selectedSingleTemplate && (
                    <span className="ml-2 normal-case tracking-normal text-brand-muted font-normal">
                      · needs {selectedSingleTemplate.placeholder_count} value{selectedSingleTemplate.placeholder_count === 1 ? "" : "s"}
                    </span>
                  )}
                </label>
                <input value={params} onChange={(e) => setParams(e.target.value)} placeholder="John, Monday 10am" className={fieldCls} data-testid="single-params" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Preview text (for your inbox log)</label>
                <input value={preview} onChange={(e) => setPreview(e.target.value)} placeholder="Auto-filled from template preview if left blank" className={fieldCls} data-testid="single-preview-log" />
              </div>
            </div>
            <button type="submit" disabled={sending || !templateName} data-testid="single-submit" className="mt-6 inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white font-display font-semibold rounded-lg px-6 py-3 transition-colors disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : "Send Template"}
            </button>
          </form>

          <aside className="lg:col-span-2 bg-white rounded-2xl border border-brand-border p-6 card-soft h-fit sticky top-6" data-testid="single-preview-panel">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-brand-blue" />
              <h2 className="font-display font-semibold text-brand-ink">Live message preview</h2>
            </div>
            <div className="bg-[#efeae2] rounded-xl p-4 min-h-[180px] flex flex-col gap-2">
              <PreviewBubble text={singlePreviewText} />
              <span className="text-[10px] text-brand-muted text-right">preview · {to || "no recipient"}</span>
            </div>
            {selectedSingleTemplate && (
              <p className="text-xs text-brand-muted mt-3">
                <MessageSquare className="w-3 h-3 inline mr-1" />
                Missing values still show as <code>{"{{1}}"}, {"{{2}}"}</code> so you can spot gaps before sending.
              </p>
            )}
          </aside>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <form onSubmit={sendBulk} data-testid="bulk-send-form" className="lg:col-span-3 bg-white rounded-2xl border border-brand-border p-6 md:p-8 card-soft">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelCls}>Template</label>
                <select
                  required
                  value={bulkTemplate ? `${bulkTemplate}::${bulkLang}` : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) { setBulkTemplate(""); return; }
                    const [n, l] = v.split("::");
                    setBulkTemplate(n);
                    setBulkLang(l);
                  }}
                  data-testid="bulk-template"
                  className={fieldCls}
                >
                  <option value="">— select an approved template —</option>
                  {templateOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls + " mb-0"}>CSV File</label>
                <button
                  type="button"
                  onClick={downloadSampleCsv}
                  data-testid="download-sample-csv"
                  className="text-xs font-display font-semibold text-brand-blue hover:underline"
                >
                  Download sample CSV
                </button>
              </div>
              <label className="flex items-center justify-between gap-4 border border-dashed border-brand-border rounded-lg px-4 py-4 cursor-pointer hover:border-brand-blue transition-colors">
                <span className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-brand-blue flex-shrink-0" />
                  <span className="text-sm text-brand-slate truncate">{file ? file.name : "Choose CSV (columns: phone, param1, param2, param3, …)"}</span>
                </span>
                <span className="text-sm font-display font-semibold text-brand-blue flex-shrink-0">Browse</span>
                <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="bulk-file" onChange={(e) => handleFileChange(e.target.files?.[0] || null)} />
              </label>
              <p className="text-xs text-brand-muted mt-2">
                Required: <code>phone</code>. Optional: <code>param1</code>, <code>param2</code>, … (mapped in order to <code>{"{{1}}"}, {"{{2}}"}</code>). The <code>name</code> column is no longer required.
              </p>
            </div>
            <button type="submit" disabled={bulkSending || !bulkTemplate} data-testid="bulk-submit" className="mt-6 inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white font-display font-semibold rounded-lg px-6 py-3 transition-colors disabled:opacity-60">
              {bulkSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {bulkSending ? "Sending…" : `Upload & Send${csvRows.length ? ` (${csvRows.length})` : ""}`}
            </button>

            {bulkResult && (
              <div className="mt-6 border-t border-brand-border pt-6" data-testid="bulk-result">
                <div className="flex gap-4 mb-4">
                  <span className="text-sm"><strong className="text-green-700">{bulkResult.sent}</strong> sent</span>
                  <span className="text-sm"><strong className="text-brand-red">{bulkResult.failed}</strong> failed</span>
                  <span className="text-sm"><strong>{bulkResult.total}</strong> total</span>
                </div>
                <div className="max-h-72 overflow-auto rounded-lg border border-brand-border divide-y divide-brand-border">
                  {bulkResult.results.map((r, i) => (
                    <div key={i} className="px-4 py-2.5 flex flex-col gap-1 text-sm" data-testid={`bulk-result-row-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-mono text-xs">{r.to}</span>
                        <span className={r.status === "sent" ? "text-green-700 font-semibold text-xs uppercase tracking-wide" : "text-brand-red font-semibold text-xs uppercase tracking-wide"}>
                          {r.status}{r.http_status ? ` · ${r.http_status}` : ""}
                        </span>
                      </div>
                      {r.status !== "sent" && r.error && (
                        <div className="text-xs text-brand-muted whitespace-pre-wrap break-words" data-testid={`bulk-result-error-${i}`}>{r.error}</div>
                      )}
                    </div>
                  ))}
                </div>
                {bulkResult.failed > 0 && (
                  <details className="mt-3 text-xs text-brand-muted">
                    <summary className="cursor-pointer font-display font-semibold text-brand-slate">Why are US numbers failing? (common causes)</summary>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      <li><strong>code 131047 · Re-engagement</strong> — recipient hasn&apos;t opted in / no 24h session. Use an <em>approved marketing template</em> and ensure the person opted in.</li>
                      <li><strong>code 131026 · Message undeliverable</strong> — number is not on WhatsApp.</li>
                      <li><strong>code 131030 · Recipient not in allowed list</strong> — your Meta app is still in <em>development mode</em>. Add the number to test recipients, or move the app to <em>Live</em>.</li>
                      <li><strong>code 132001 · Template not found</strong> — template name/language mismatch. US templates usually use <code>en_US</code>, not <code>en</code>.</li>
                      <li><strong>code 132000 · Number of parameters mismatch</strong> — your CSV has fewer/more <code>paramN</code> columns than the template expects.</li>
                      <li><strong>code 80007 / 130429 · Rate limit</strong> — too many sends per second. Space them out.</li>
                      <li><strong>Bad phone format</strong> — US numbers must be <code>1XXXXXXXXXX</code> (11 digits, no <code>+</code>, no dashes). We now auto-strip formatting, but very old exports might still have issues.</li>
                    </ul>
                  </details>
                )}
              </div>
            )}
          </form>

          <aside className="lg:col-span-2 bg-white rounded-2xl border border-brand-border p-6 card-soft h-fit sticky top-6" data-testid="bulk-preview-panel">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-brand-blue" />
              <h2 className="font-display font-semibold text-brand-ink">Live message preview</h2>
            </div>
            {!selectedBulkTemplate ? (
              <div className="bg-[#efeae2] rounded-xl p-4 min-h-[140px] flex items-center justify-center">
                <PreviewBubble empty="Select a template to preview" />
              </div>
            ) : bulkPreviewRows.length === 0 ? (
              <div className="bg-[#efeae2] rounded-xl p-4 min-h-[140px] flex flex-col gap-2">
                <PreviewBubble
                  text={buildFullMessage(selectedBulkTemplate, [])}
                />
                <span className="text-[10px] text-brand-muted text-right">
                  {file ? "no valid rows parsed" : "upload a CSV to see per-recipient previews"}
                </span>
              </div>
            ) : (
              <div className="space-y-4 max-h-[520px] overflow-auto pr-1">
                {bulkPreviewRows.map((r, i) => (
                  <div key={i} className="bg-[#efeae2] rounded-xl p-3" data-testid={`bulk-preview-row-${i}`}>
                    <div className="text-[11px] font-mono text-brand-muted mb-1.5">To: {r.phone}</div>
                    <PreviewBubble text={buildFullMessage(selectedBulkTemplate, r.params)} />
                  </div>
                ))}
                {bulkPreviewMore > 0 && (
                  <div className="text-xs text-brand-muted text-center pt-1">+ {bulkPreviewMore} more recipient{bulkPreviewMore === 1 ? "" : "s"} …</div>
                )}
              </div>
            )}
            {selectedBulkTemplate && (
              <p className="text-xs text-brand-muted mt-3">
                Missing values remain as <code>{"{{n}}"}</code> so you can catch typos before Meta bills you for failed sends.
              </p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default SendMessages;
